import React, {ChangeEvent, useEffect, useState, useMemo} from 'react';
import b_ from 'b_';
import {useFormContext} from 'react-hook-form';

import sumBy from 'lodash/sumBy';
import isEqual from 'lodash/isEqual';
import cloneDeep from 'lodash/cloneDeep';

import {Select, Button, Icon} from 'client/common/components/lego';

import {Quota} from 'client/common/types';

import {useBoxType} from 'client/common/hooks/box';

import {useSurveySlug} from 'client/common/contexts/slug';

import {ValidationErrorType, setError, unsetError} from 'client/common/utils/errors';

import I18N from 'assets/i18n/brandlift-project';

import AgeMatrix from './age-matrix';
import GenderMatrix from './gender-matrix';
import AllMatrix from './all-matrix';

import {
    DEFAULT_AGE_QUOTAS,
    DEFAULT_ALL_QUOTAS,
    DEFAULT_GENDER_QUOTAS,
    genRequestQuotas,
    getQuotasType,
    ParsedQuota,
    parseQuotas,
    QuotasType
} from './utils';

import './quotas.css';

const i18n = I18N.common.audienceSettings.quotas;
const b = b_.with('quotas');

export interface QuotasProps {
    quotas: Quota[];
    updateQuotas: (quotas: Quota[]) => Promise<void>;
}

const QUOTAS_TYPE_OPTIONS = [
    {
        value: 'age',
        content: i18n.type.age
    },
    {
        value: 'gender',
        content: i18n.type.gender
    },
    {
        value: 'all',
        content: i18n.type.all
    }
];

const DEFAULT_QUOTAS_BY_TYPE = {
    age: DEFAULT_AGE_QUOTAS,
    gender: DEFAULT_GENDER_QUOTAS,
    all: DEFAULT_ALL_QUOTAS
};

const MATRIX_BY_TYPE = {
    age: AgeMatrix,
    gender: GenderMatrix,
    all: AllMatrix
};

function useFormRegister(quotas: ParsedQuota[]) {
    const formContext = useFormContext<{quotas: any}>();

    return {
        // eslint-disable-next-line @typescript-eslint/unbound-method
        register: formContext?.register,
        rules: {
            validate: () => {
                const sizeSum = sumBy(quotas, ({sizePerc}) => sizePerc);

                return sizeSum === 100;
            }
        }
    };
}

// eslint-disable-next-line max-statements
export default function Quotas(props: QuotasProps) {
    const {updateQuotas} = props;

    const parsedQuotas: ParsedQuota[] = parseQuotas(props.quotas);
    const [quotas, setQuotas] = useState(parsedQuotas);
    const quotasType = getQuotasType(quotas);
    const slug = useSurveySlug();
    const boxType = useBoxType(slug);

    const [errorMsg, setErrorMsg] = useState<string>();

    // Провязываем форму с квотами, если контекст формы существует
    const {register, rules} = useFormRegister(quotas) || {};

    useEffect(() => {
        const sizeSum = sumBy(quotas, ({sizePerc}) => sizePerc);

        if (sizeSum !== 100) {
            setError('quotas', ValidationErrorType.InvalidQuotas);

            return setErrorMsg(i18n.error.sum);
        }

        unsetError('quotas');

        setErrorMsg(undefined);
    }, [quotas, setErrorMsg]);

    // Привязываем изменение квот из props
    useEffect(() => {
        const newQuotas: ParsedQuota[] = parseQuotas(props.quotas);

        if (!isEqual(newQuotas, quotas)) {
            setQuotas(newQuotas);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.quotas, setQuotas]);

    useEffect(() => {
        return () => unsetError('quotas');
    }, []);

    async function onChangeQuotasType(e: ChangeEvent<HTMLSelectElement>) {
        await updateQuotas(genRequestQuotas(DEFAULT_QUOTAS_BY_TYPE[e.target.value as QuotasType]));
    }

    async function resetQuotas() {
        await updateQuotas(genRequestQuotas(DEFAULT_GENDER_QUOTAS));
    }

    async function pasteDefaultQuotas() {
        if (!quotasType) {
            return;
        }

        await updateQuotas(genRequestQuotas(DEFAULT_QUOTAS_BY_TYPE[quotasType]));
    }

    async function saveQuotas() {
        if (errorMsg) {
            return;
        }

        const requestQuotas = genRequestQuotas(quotas);

        await updateQuotas(requestQuotas);
    }

    function onChange({gender, age, sizePerc}: {gender?: string; age?: string; sizePerc: string}) {
        let validSizePerc = parseInt(sizePerc.toString(), 10);

        if (!validSizePerc || validSizePerc < 0) {
            validSizePerc = 0;
        }

        const index = quotas.findIndex(quota => {
            if (gender && quota.gender !== gender) {
                return false;
            }

            if (age && quota.age !== age) {
                return false;
            }

            return true;
        });

        setQuotas(old => {
            if (index >= 0) {
                const newQuotas = cloneDeep(old);

                newQuotas[index].sizePerc = validSizePerc;

                return newQuotas;
            }

            return old.concat([
                {
                    sizePerc: validSizePerc,
                    ...(age && {age}),
                    ...(gender && {gender})
                }
            ]);
        });
    }

    const isPasteDefaultVisible = useMemo(
        () => quotasType && !isEqual(quotas, DEFAULT_QUOTAS_BY_TYPE[quotasType]),
        [quotas, quotasType]
    );

    if (!quotasType) {
        return (
            <div className={b()}>
                <p className={b('warning')}>{i18n.invalidSettings}</p>
                <Button size='m' view='dl-default' onClick={resetQuotas}>
                    {i18n.reset}
                </Button>
            </div>
        );
    }

    const MatrixComponent = MATRIX_BY_TYPE[quotasType];

    let tipTitle: string = i18n.tip;

    if (!boxType) {
        tipTitle = i18n.proTip;
    }

    return (
        <div className={b()}>
            {boxType !== 'ux' && <div className={b('tip')}>{tipTitle}</div>}
            <Select
                options={QUOTAS_TYPE_OPTIONS}
                value={quotasType}
                onChange={onChangeQuotasType}
                size='m'
                view='dl-clear'
                width='max'
                multiple={false}
            />
            <MatrixComponent
                register={register}
                rules={rules}
                quotas={quotas}
                error={errorMsg}
                onChange={onChange}
                onBlur={saveQuotas}
            />
            {errorMsg && <p className={b('error error-label')}>{errorMsg}</p>}
            {isPasteDefaultVisible && (
                <Button
                    className={b('revert-btn')}
                    iconLeft={(cls: string) => (
                        <Icon className={cls} type='refresh-black' size='m' />
                    )}
                    onClick={pasteDefaultQuotas}
                    size='m'
                    view='dl-light'
                >
                    {i18n.pasteData}
                </Button>
            )}
        </div>
    );
}
